import axios from 'axios'
import jwtDecode from 'jwt-decode'
import initializeApp from 'utils/initializeApp'

const { AUTH_URL } = process.env

const REFRESH_PATH = 'user/token/refresh'
// Refresh a little early so an in-flight request never lands on an expired token.
const EXPIRY_SKEW_MS = 5 * 60 * 1000

let umsRequest
let onToken = () => {}
let inFlight = null

const portalToken = () => umsRequest?.defaults?.headers?.common?.Authorization

const isExpiringSoon = token => {
  try {
    return jwtDecode(token).exp * 1000 - Date.now() < EXPIRY_SKEW_MS
  } catch {
    return false
  }
}

const isRefreshCall = config => (config?.url || '').includes(REFRESH_PATH)

const explicitToken = config => config?.headers?.Authorization
const requestToken = config =>
  explicitToken(config) ?? config?.headers?.common?.Authorization

const persistToken = token => {
  initializeApp(token)
  localStorage.setItem('token', token)
  if (sessionStorage.getItem('token')) {
    sessionStorage.setItem('token', token)
  }
  onToken(token)
}

const endSession = () => {
  localStorage.clear()
  sessionStorage.removeItem('token')
  window.location.replace(AUTH_URL)
}

export const refreshAccessToken = () => {
  if (!inFlight) {
    inFlight = axios
      // The body must be an object even though the refresh needs no payload:
      // axios labels a null body application/x-www-form-urlencoded, which the
      // auth service rejects with 415 before the handler ever runs.
      .post(
        REFRESH_PATH,
        {},
        {
          baseURL: umsRequest.defaults.baseURL,
          withCredentials: true,
        }
      )
      .then(response => {
        const token = response?.data?.data?.token
        if (!token) {
          throw new Error('Refresh response had no token')
        }
        persistToken(token)
        return token
      })
      .catch(error => {
        endSession()
        throw error
      })
      .finally(() => {
        inFlight = null
      })
  }
  return inFlight
}

// Silently renews the portal access token on every axios instance: before a
// request when the token is about to expire, and once more on a 401.
// Requests carrying a foreign token (e.g. invite-scoped JWTs) are left alone.
export const installSessionRefresh = ({ umsRequest: ums, instances, onToken: handler }) => {
  umsRequest = ums
  onToken = handler || onToken
  ums.defaults.withCredentials = true

  instances.forEach(instance => {
    instance.interceptors.request.use(async config => {
      const portal = portalToken()
      const current = requestToken(config)
      if (!portal || isRefreshCall(config) || (current && current !== portal)) {
        return config
      }
      const fresh = isExpiringSoon(portal) ? await refreshAccessToken() : portal
      config.__portalToken = fresh
      // Keep the token at the level it came from so per-request overrides added
      // by later interceptors (e.g. invite-scoped JWTs) keep their precedence.
      if (explicitToken(config)) {
        config.headers.Authorization = fresh
      } else {
        config.headers.common.Authorization = fresh
      }
      return config
    })

    instance.interceptors.response.use(undefined, async error => {
      const config = error?.config
      const sentPortalToken =
        config?.__portalToken && config.headers?.Authorization === config.__portalToken
      if (error?.response?.status !== 401 || !sentPortalToken || config.__retried || !portalToken()) {
        throw error
      }
      config.__retried = true
      // Another tab or request may already have rotated the token; reuse it.
      const isStale = config.__portalToken !== portalToken()
      config.headers.Authorization = isStale ? portalToken() : await refreshAccessToken()
      return instance.request(config)
    })
  })
}
